<template>
  <div
    id="task-panel"
    :class="[activePanel === 'searchOmenReport' && 'is-active']"
    class="right-panel-wrapper is-task"
  >
    <div class="panel-overlay" @click="activePanel = 'none'"></div>
    <div class="right-panel">
      <div class="right-panel-head">
        <h3>Omen研报</h3>
        <transition name="fade-fast">
          <V-Button
            v-if="selectedItemsCount>0"
            icon="fas fa-check"
            color="primary"
            raised
            class="btn-choose"
            :loading="props.loading"
            @click="handleConfirmSelect"
          >
            <span>{{ t('action-select') }}</span>
          </V-Button>
        </transition>
        <a class="close-panel" @click="activePanel = 'none'">
          <i
            aria-hidden="true"
            class="iconify"
            data-icon="feather:chevron-right"
          ></i>
        </a>
      </div>
      <div class="right-panel-body has-slimscroll" data-simplebar>
        <div class="search-layout">
          <transition name="fade-fast">
            <VDropdown v-if="selectedItemsCount>0" ref="selectList" class="select-button">
              <template #button="{ open, toggle }">
                <VIconBox color="primary" rounded @click="toggle">
                  <i class="fas fa-shopping-cart"></i>
                  <div class="badge">{{selectedItemsCount}}</div>
                </VIconBox>
              </template>
              <template>
                <a v-for="item in selectedContents" :key="item.id + item.name" role="menuitem"
                   class="dropdown-item is-media">
                  <i class="fas fa-box" aria-hidden="true"></i>
                  <p class="item-title">{{item.name}}</p>
                  <i class="fas fa-trash-alt item-delete" @click="removeSelectedContent(item)"></i>
                </a>
              </template>
            </VDropdown>
          </transition>
          <transition name="fade-fast">
            <V-Control icon="feather:search" class="search-input">
              <input
                ref="keywordsInput"
                v-model="searchKeywords"
                class="input is-rounded search-input"
                :placeholder="t('placeholder-search')"
                @keyup.enter="handleSearch"
              />
            </V-Control>
          </transition>
        </div>
        <div>
          <article-simple-list
						:data="articleList"
						:total-count="result?.contents?.total_count"
						:page-size="searchParam.limit"
						:loading="loading"
						:page="searchParam.page"
						:page-link-display="5"
						:show-empty-placeholder="!inputingValue && searchParam.keywords !== ''"
						:is-multi-select="props.isMultiSelect"
						@itemClick="handleContentClick"
						@onPageChange="onSearchPageChange"
					>
            <template #title="{ item }">
              <div class="flex items-center justify-between">
                <div class="text-base line-clamp-3">
                  {{ getReportTitle(item) }}
                </div>
                <transition name="fade-fast">
                  <i v-if="item.select" class="iconfont icon-check text-[var(--primary)]"></i>
                </transition>
                </div>
            </template>

            <template #bottom="{ item }">
              <div v-if="(getStackedTags(item)?.length??0) > 0" class="flex flex-wrap gap-2 mt-2.5">
                <div 
                  v-for="tag in getStackedTags(item)" 
                  :key="tag"
                  class="text-xs rounded-full bg-[var(--white)] px-2 py-1 shrink-0 border border-solid border-[var(--fade-grey-dark-3)]"
                >
                  {{ tag }}
                </div>
              </div>
            </template>
          </article-simple-list>
        </div>
      </div>

    </div>
  </div>
</template>

<script setup lang="ts">
import {computed, reactive, ref, toRefs, watch} from 'vue'
import { useI18n } from 'vue-i18n'
import { activePanel } from '/@src/state/activePanelState'
import { SearchRequestParam, ContentKind, TagID, TagGroup } from "/@src/models/api";
import {ContentStatusValue} from "/@src/models/content";
import {useContentsList} from "/@src/composable/content/useContentsList";
import {RelationKind,Content} from '/@src/__generated__/generated-gql-types';
import _ from "lodash";
import { useReportUtils } from '/@src/composable/useReportUtils';

//props
const props = defineProps({
  isMultiSelect:{
    type:Boolean,
    default:false
  },
  loading:{
    type:Boolean,
    default:false
  },
	/**
   * 是否状态必须为已发布
   */
  requirePublish:{
    type:Boolean,
    default:false
  },
})

//data
const { t } = useI18n()
const articleList = ref<Array<Content>>([])
const propsData = toRefs(props)
const searchKeywords = ref('')
const isLoading = ref(false)
const inputingValue = ref(false)
const {getReportTitle,getStackedTags} = useReportUtils()
const searchParam: SearchRequestParam = reactive({
	keywords: '',
  limit: 10,
  page: 1
})
const selectedContents = reactive<Array<any>>([])
const contentsParam = ref({
  first: searchParam.limit,
  kinds: [ContentKind.REPORT],
  relations: [{
    kind:RelationKind.None,
    groups:[TagGroup.DOCUMENT_IB_REPORT],
  }],
  status: ContentStatusValue.PUBLISHED
})

const {loading,result,page,loadData,refetch} = useContentsList(contentsParam.value,{
  lazy: true
})

//watch
watch(() => result.value?.contents?.items, (newItems) => {
  if (newItems && newItems.length > 0) {
    const content = _.cloneDeep(newItems)
    articleList.value = content.map(item => ({
      ...item,
      select: false // 添加 select
    }))
  } else {
    articleList.value = []
  }
})

watch(activePanel, async (nv) => {
  if(nv === 'searchOmenReport') {
    handleSearch()
  }
})

watch(propsData.isMultiSelect, () => {
  clearSelectedContents()
})

watch(searchKeywords, () => {
  if (searchKeywords.value) {
    searchParam.page = 1
  }
})

//emit
const emit = defineEmits<{
  (event:'onSelectContents',data:Array<Content>):void
}>()

//computed
const selectedItemsCount = computed(() => {
  return selectedContents.length
})

//methods
const handleSearch = async () => {
  isLoading.value = true
  inputingValue.value = false
  searchParam.keywords = searchKeywords.value
  contentsParam.value = {
    ...contentsParam.value,
    track: searchParam.keywords,
    offset: (searchParam.page-1)*searchParam.limit
  }
  loadData(contentsParam.value)
  setTimeout(() => {
    isLoading.value = false
  }, (200))
}

const onSearchPageChange = (page: number) => {
  searchParam.page = page
	handleSearch(page)
}

const handleContentClick = (item: Content)=>{
  if(props.isMultiSelect){
    toggleContentSelect(item)
  }else {
    singleContentSelect(item)
  }
}

const toggleContentSelect = (item:Content)=>{
  item.select = !item.select
  if(item.select){
    selectedContents.unshift(item)
  }else {
    removeSelectedContent(item)
  }
}

const singleContentSelect = (item:Content)=>{
  item.select = !item.select
  if(item.select){
    if(selectedContents[0]&&selectedContents[0] !== item){
      selectedContents[0].select = false
      selectedContents.splice(0,1)
    }
    selectedContents.push(item)
  }else {
    selectedContents.splice(0,1)
  }
}

const removeSelectedContent = (item:Content)=>{
  let find = -1
  for(let index=0;index<selectedContents.length;index++){
    const data = selectedContents[index]
    if(data.id === item.id && data.kind === item.kind){
      item.select = false
      find = index
      break
    }
  }
  if(find>=0){
    selectedContents.splice(find,1)
  }
}

const handleConfirmSelect = () => {
  const content = JSON.parse(JSON.stringify(selectedContents))
  emit('onSelectContents',content)
  activePanel.value = 'none'
  clearSelectedContents()
}

const clearSelectedContents = ()=>{
  if(selectedContents.length>0){
    selectedContents.forEach(it=>{
      it.select = false
    })
  }
  selectedContents.splice(0,selectedContents.length)
}

defineExpose({clearSelectedContents})
</script>

<style lang="scss">
@import '../../../scss/abstracts/_variables.scss';
@import '../../../scss/abstracts/_mixins.scss';
@import '../../../scss/layout/_right-panel.scss';
.right-panel-wrapper {
  .right-panel-head {
    .btn-choose {
      margin-left: auto;
    }
  }
  .right-panel-body {
    .search-layout {
      display: flex;
      .select-button {
        cursor: pointer;
        margin-right: 10px;
        .fas {
          font-size: 14px;
        }
        .dropdown-item {
          display: flex;
          width: 20rem;
          align-items: center;
          padding-right: 8px;
          .item-title {
            @include textLine(1);

            color: var(--light-text);
            font-weight: 400;
            font-size: 0.8rem;
            margin-left: 4px;
            padding-right: 4px;
          }
          .item-delete {
            margin-left: auto;
            padding: 4px;
            //color: var(--danger);
          }
        }
        .dropdown-item:hover .item-title {
          color: var(--dark-text);
        }
      }
      .badge {
        position: absolute;
        width: 18px;
        height: 18px;
        background: var(--danger);
        top: 1px;
        right: -5px;
        text-align: center;
        font-size: 11px;
        font-weight: 500;
        color: white;
        box-shadow: var(--light-box-shadow);
        border-radius: var(--radius-rounded);
      }
      .search-input {
        flex: 1;
        transition: width 1s linear;
				margin-bottom: 10px;
      }
    }
  }
}
</style>
